import * as React from 'react';

import type { AjaxError } from 'rxjs';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import type { HandleAjaxError } from '@helpcenter/flags';
import { h600, h700 } from '@helpcenter/styles/src/fonts';
import { ThemedButton } from '@helpcenter/themed-button';
import type { MessageDescriptor, WrappedComponentProps } from 'react-intl';
import { FormattedMessage, injectIntl } from 'react-intl';
import { forgotPassword } from 'rest/login';
import type { resetLogin } from 'state/actions/login';
import ThemedBackButton from 'view/common/themed-back-button';
import Heading from '@atlaskit/heading';
import { Box, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import { UseTypographyMigratedComponent } from '@atlassian/help-center-common-util/enable-typography-migration';

import { getRedirectUrlForSignupAndResetPasswordRequestParam } from '@atlassian/help-center-common-util/url/url';
import loginMessages from '../messages';
import messages from './messages';

type MessageValue = string | number | boolean | Date | null | undefined;
export interface ForgotPasswordConfirmationProps {
	email: string;
	resetLoginAction: typeof resetLogin;
	helpCenterName: string;
	resetUrl: string;
	baseUrl: string;
	onBackButtonClick: () => void;
	onResendLinkError: (error: AjaxError) => HandleAjaxError;
	onResendSuccess: (
		titleMessage: MessageDescriptor,
		titleMessageValues?: { [key: string]: MessageValue },
	) => void;
}

interface ForgotPasswordConfirmationPropsState {
	isSendingForgotPasswordEmail: boolean;
}

export class ForgotPasswordConfirmation extends React.Component<
	ForgotPasswordConfirmationProps & WrappedComponentProps,
	ForgotPasswordConfirmationPropsState
> {
	state = {
		isSendingForgotPasswordEmail: false,
	};
	getRedirectUrl = (baseUrl: string) => {
		return getRedirectUrlForSignupAndResetPasswordRequestParam(baseUrl);
	};
	onResendButtonClick = () => {
		this.setState({ isSendingForgotPasswordEmail: true });

		const redirectUrl = this.getRedirectUrl(this.props.baseUrl);

		forgotPassword(this.props.resetUrl, this.props.email, redirectUrl)
			.toPromise()
			.then(() => {
				this.setState({ isSendingForgotPasswordEmail: false });
				this.props.onResendSuccess(messages.resendSuccessMessage, {
					emailAddress: this.props.email,
				});
			})
			.catch((err) => {
				this.setState({ isSendingForgotPasswordEmail: false });
				this.props.onResendLinkError(err);
			});
	};
	renderContent = () => {
		const { intl, email, helpCenterName, onBackButtonClick } = this.props;

		return (
			<>
				<ThemedBackButton
					appearance="link"
					onClick={onBackButtonClick}
					actionSubjectId="returnToLogin"
				>
					{intl.formatMessage(loginMessages.backButton)}
				</ThemedBackButton>
				<UseTypographyMigratedComponent
					migratedComponent={
						<Box paddingBlockEnd="space.300">
							<Heading size="large">{helpCenterName}</Heading>
						</Box>
					}
				>
					<Title>{helpCenterName}</Title>
				</UseTypographyMigratedComponent>
				<UseTypographyMigratedComponent
					migratedComponent={
						<Heading size="medium">
							<FormattedMessage {...messages.checkYourEmail} />
						</Heading>
					}
				>
					<Subtitle>
						<FormattedMessage {...messages.checkYourEmail} />
					</Subtitle>
				</UseTypographyMigratedComponent>
				<UseTypographyMigratedComponent
					migratedComponent={
						<Text>
							{intl.formatMessage(messages.emailHasBeenSentMessage, { emailAddress: email })}
						</Text>
					}
				>
					<div>{intl.formatMessage(messages.emailHasBeenSentMessage, { emailAddress: email })}</div>
				</UseTypographyMigratedComponent>
				<ThemedButton
					appearance={'primary'}
					onClick={this.onResendButtonClick}
					actionSubjectId="returnToLogin"
					shouldFitContainer
					isLoading={this.state.isSendingForgotPasswordEmail}
				>
					<FormattedMessage {...messages.resendResetPasswordLinkLabel} />
				</ThemedButton>
			</>
		);
	};
	render() {
		return (
			<Container data-test-id="forgot-password-container">
				<FireScreenEventOnMount />
				{this.renderContent()}
			</Container>
		);
	}
}

export default injectIntl(ForgotPasswordConfirmation);

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Container = styled.div`
	> * {
		margin-bottom: ${token('space.300', '24px')};
	}

	> *:last-child {
		margin-bottom: 0;
	}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Title = styled.div`
	${h700};
	margin-bottom: ${token('space.300', '24px')};
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Subtitle = styled.div`
	${h600};
`;
